import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Phone, Zap, ArrowRight, Check, Bot, Users,
  Calendar, Brain, Shield, MessageSquare, Star,
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BENEFITS = [
  { icon: Phone, title: 'AI answers every call', desc: 'Never miss a lead. Your AI picks up 24/7 with a professional, warm voice.' },
  { icon: Users, title: 'Qualifies leads automatically', desc: 'Captures name, budget, timeline, and area — scores leads as hot, warm, or cold.' },
  { icon: Calendar, title: 'Books appointments', desc: 'Detects scheduling intent and books showings directly into your Google Calendar.' },
  { icon: Brain, title: 'Learns your market', desc: 'Pre-loaded with real estate knowledge. Answers buyer, seller, and listing questions accurately.' },
  { icon: MessageSquare, title: 'SMS follow-ups', desc: 'Automated text follow-ups keep leads engaged after every call.' },
  { icon: Shield, title: 'Fair housing compliant', desc: 'Built-in compliance guardrails for fair housing, legal, and lending deflection.' },
];

export default function ReferralLandingPage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [referral, setReferral] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/growth/r/${code}`)
      .then(({ data }) => {
        setReferral(data);
        axios.post(`${API}/growth/r/track-click`, { code, referrer: document.referrer }).catch(() => {});
      })
      .catch(() => setReferral(null))
      .finally(() => setLoading(false));
  }, [code]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="h-8 w-8 rounded-full border-2 border-emerald-500 border-t-transparent animate-spin" />
      </div>
    );
  }

  const signupUrl = `/signup${code ? `?ref=${code}` : ''}`;

  return (
    <div className="min-h-screen bg-background" data-testid="referral-landing-page">
      {/* Hero */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-500/5 to-transparent" />
        <div className="max-w-4xl mx-auto px-4 pt-16 pb-12 text-center relative z-10">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-medium mb-6" data-testid="referral-badge">
            <Star size={12} />
            {referral ? `Invited by ${referral.referred_by}` : 'Welcome to Agent9'}
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4 leading-tight">
            Your AI real estate assistant
            <br />
            <span className="text-emerald-400">that never sleeps</span>
          </h1>

          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            Agent9 answers your calls, qualifies leads, books appointments, and follows up with clients — all while you focus on closing deals.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
            <Button onClick={() => navigate(signupUrl)} size="lg"
              className="bg-emerald-500 hover:bg-emerald-600 text-white h-12 px-8 text-base gap-2 rounded-xl"
              data-testid="referral-cta-signup">
              <Zap size={18} /> Start Free Trial <ArrowRight size={16} />
            </Button>
            {referral?.agent_slug && (
              <Button onClick={() => navigate(`/demo/${referral.agent_slug}`)} size="lg" variant="outline"
                className="border-border text-foreground hover:text-foreground hover:bg-muted h-12 px-8 text-base gap-2 rounded-xl"
                data-testid="referral-cta-demo">
                <Bot size={18} /> Try the Demo
              </Button>
            )}
          </div>

          {referral?.reward_info && (
            <p className="text-xs text-muted-foreground mt-4">{referral.reward_info}</p>
          )}
        </div>
      </div>

      {/* Benefits */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <h2 className="text-lg font-semibold text-foreground text-center mb-8">Everything your AI assistant handles</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {BENEFITS.map((b, i) => (
            <div key={i} className="p-4 rounded-xl bg-card border border-border hover:border-border transition-colors" data-testid={`benefit-${i}`}>
              <div className="h-9 w-9 rounded-lg bg-emerald-500/8 border border-emerald-500/15 flex items-center justify-center mb-3">
                <b.icon size={18} className="text-emerald-400" />
              </div>
              <h3 className="text-sm font-semibold text-foreground mb-1">{b.title}</h3>
              <p className="text-xs text-muted-foreground leading-relaxed">{b.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Social Proof */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="rounded-xl bg-gradient-to-br from-card to-card border border-border p-6 text-center">
          <div className="flex justify-center gap-8 mb-4">
            {[
              { val: '10K+', label: 'Calls handled' },
              { val: '2,500+', label: 'Leads qualified' },
              { val: '850+', label: 'Appointments booked' },
            ].map((s, i) => (
              <div key={i}>
                <p className="text-xl sm:text-2xl font-bold text-foreground">{s.val}</p>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{s.label}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">Trusted by real estate professionals across the US</p>
        </div>
      </div>

      {/* Pricing teaser */}
      <div className="max-w-4xl mx-auto px-4 py-8 text-center">
        <h2 className="text-lg font-semibold text-foreground mb-2">Start free. Scale when ready.</h2>
        <p className="text-sm text-muted-foreground mb-6">7-day free trial. No credit card required. Plans from $129/mo.</p>
        <Button onClick={() => navigate(signupUrl)}
          className="bg-emerald-500 hover:bg-emerald-600 text-white h-11 px-6 text-sm gap-2 rounded-xl"
          data-testid="referral-bottom-cta">
          <Zap size={14} /> Create Your AI Assistant
        </Button>
      </div>

      {/* Footer */}
      <div className="border-t border-border py-6 text-center">
        <p className="text-[10px] text-muted-foreground">
          Agent9 &mdash; AI assistants for real estate professionals
        </p>
      </div>
    </div>
  );
}
